import React from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import usePageTitle from "../hooks/usePageTitle";
import { useTranslation, Trans } from "react-i18next";
import "./PricingPage.css";

const TIERS = [
  {
    id: "free",
    name: "Free Tier",
    price: "0",
    icon: "🆓",
    highlight: false,
    color: "#b9bbbe",
  },
  {
    id: "premium",
    name: "Premium Tier",
    price: "9.99",
    icon: "⭐",
    highlight: true,
    color: "#ffd700",
  },
  {
    id: "business",
    name: "Business Tier",
    price: "29.99",
    icon: "🚀",
    highlight: false,
    color: "#E53935",
  },
];

function PricingPage() {
  const { t } = useTranslation();
  usePageTitle(t("pricing.pageTitle"));
  const navigate = useNavigate();

  const handleSelectTier = (tierId) => {
    trackEvent("select_pricing_plan", {
      plan_id: tierId,
      plan_name: TIERS.find((t) => t.id === tierId)?.name,
    });

    if (tierId === "free") {
      navigate("/profile");
    } else {
      navigate(`/checkout/${tierId}`);
    }
  };

  return (
    <div className="pricing-page">
      {/* Background Decorativo */}
      <div className="bg-glow glow-1"></div>
      <div className="bg-glow glow-2"></div>
      <div className="bg-glow glow-3"></div>

      <div className="pricing-hero">
        <div className="container">
          <h1 className="hero-title">
            <Trans
              i18nKey="pricing.heroTitle"
              defaults="Escolha o Plano Ideal para seu <span className='gradient-text'>Bot</span>"
              components={{ span: <span className="gradient-text" /> }}
            />
          </h1>
          <p className="hero-subtitle">{t("pricing.heroSubtitle")}</p>
        </div>
      </div>

      <section className="pricing-section">
        <div className="container">
          <div className="pricing-grid">
            {TIERS.map((tier) => (
              <div key={tier.id} className={`pricing-card ${tier.highlight ? "highlight" : ""}`} style={{ "--tier-color": tier.color }}>
                {tier.highlight && (
                  <div className="popular-badge">
                    <span>{t("pricing.popular")}</span>
                  </div>
                )}

                <div className="card-header">
                  <div className="icon-wrapper">
                    <span className="tier-icon">{tier.icon}</span>
                  </div>
                  <h3 className="tier-name">{tier.name}</h3>
                  <div className="tier-price">
                    <span className="currency">$</span>
                    <span className="amount">{tier.price}</span>
                    <span className="period">{t("pricing.period")}</span>
                  </div>
                  <p className="tier-desc">{t(`pricing.tiers.${tier.id}.description`)}</p>
                </div>

                <div className="card-features">
                  <ul>
                    {t(`pricing.tiers.${tier.id}.features`, {
                      returnObjects: true,
                    }).map((feature, idx) => (
                      <li key={idx}>
                        <i className="check-mark">✓</i> {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-footer">
                  <button className="btn btn-block upgrade-btn" onClick={() => handleSelectTier(tier.id)}>
                    {t(`pricing.tiers.${tier.id}.button`)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-faq">
        <div className="container">
          <h2 className="section-title">{t("pricing.sectionTitle")}</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>{t("pricing.faq.q1")}</h4>
              <p>
                <Trans
                  i18nKey="pricing.faq.a1"
                  defaults="Não, os planos são aplicados a um <strong>Bot específico</strong>. Caso você tenha vários bots, precisará adquirir um plano para cada um."
                  components={{ 0: <strong /> }}
                />
              </p>
            </div>
            <div className="faq-item">
              <h4>{t("pricing.faq.q2")}</h4>
              <p>{t("pricing.faq.a2")}</p>
            </div>
            <div className="faq-item">
              <h4>{t("pricing.faq.q3")}</h4>
              <p>{t("pricing.faq.a3")}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PricingPage;
